import { useRef, useState, useCallback, useEffect } from "react";
import Swal from "sweetalert2";

// Guarda solo, sin boton. Los cambios de una misma fila se juntan en una sola
// peticion: cada edicion reinicia el reloj y se manda cuando la fila se queda quieta.
// `inmediato` es para las casillas de palomita, donde no tiene caso esperar.
//
// `guardarFila` recibe la fila ya actualizada y debe devolver una promesa.
export function useAutoGuardado(guardarFila, opciones = {}) {
  const { retardo = 800, tituloError = "No se guardo", textoError = "Revisa tu conexion" } = opciones;
  const timers = useRef({});
  const limpiezas = useRef({});
  const pendientes = useRef({});
  const guardarRef = useRef(guardarFila);
  guardarRef.current = guardarFila;

  // id de fila -> "guardando" | "guardado" | "error"
  const [estado, setEstado] = useState({});
  const marca = (id, v) => setEstado((p) => ({ ...p, [id]: v }));

  const enviar = useCallback((id) => {
    const fila = pendientes.current[id];
    if (!fila) return;
    delete pendientes.current[id];
    marca(id, "guardando");
    Promise.resolve(guardarRef.current(fila))
      .then(() => {
        marca(id, "guardado");
        // el "guardado" se apaga solo para no dejar la tabla llena de palomitas verdes
        clearTimeout(limpiezas.current[id]);
        limpiezas.current[id] = setTimeout(
          () => setEstado((p) => { const n = { ...p }; delete n[id]; return n; }),
          2500
        );
      })
      .catch((error) => {
        marca(id, "error");
        Swal.fire({
          toast: true, position: "top-end", icon: "error", timer: 5000,
          showConfirmButton: false, title: tituloError,
          text: error?.response?.data?.message || error?.message || textoError,
        });
      });
  }, [tituloError, textoError]);

  const programa = useCallback((fila, inmediato = false) => {
    const id = fila._id;
    pendientes.current[id] = fila;
    clearTimeout(timers.current[id]);
    if (inmediato) enviar(id);
    else timers.current[id] = setTimeout(() => enviar(id), retardo);
  }, [enviar, retardo]);

  // si el usuario se sale con algo escrito a medias, se manda antes de desmontar
  useEffect(() => {
    const t = timers.current, l = limpiezas.current, p = pendientes.current;
    return () => {
      Object.keys(t).forEach((id) => {
        clearTimeout(t[id]);
        if (p[id]) guardarRef.current(p[id]);
      });
      Object.values(l).forEach(clearTimeout);
    };
  }, []);

  return { programa, estado };
}

// Colores de fondo del renglon segun como va su guardado.
export const fondoFila = (e) =>
  e === "guardando" ? "#fffbeb" : e === "guardado" ? "#f0fdf4" : e === "error" ? "#fef2f2" : undefined;

// Marquita al lado del folio. La vista de proveedor va en ingles.
export const SELLO_ES = { guardando: "guardando...", guardado: "guardado", error: "no se guardo" };
export const SELLO_EN = { guardando: "saving...", guardado: "saved", error: "not saved" };
export const COLOR_SELLO = { guardando: "#b45309", guardado: "#15803d", error: "#b91c1c" };
